import { Link as RouterLink } from 'react-router';
import { NATION_LABELS, type Nation } from '@seventide/types';
import { useOptionalWorkspace } from '../../hooks/useWorkspace';
import { useAccessBlock } from '../../hooks/useCan';
import { useReadOnlyFlag } from '../../lib/readOnly';
import { Banner } from '../ui/Alert';
import { partialRulesFor, type NationBannerModule } from './nationRules';

export interface NationBannerProps {
  module: NationBannerModule;
  /** Override the workspace nation (e.g. on onboarding). */
  nation?: Nation;
}

/**
 * "Scotland rules: partial" banner (R-4.2) for modules whose rules are only fully implemented for England.
 * Renders nothing for England, or for modules whose rules are UK-wide (IHT/gifts, money, documents).
 */
export function NationBanner({ module, nation: nationProp }: NationBannerProps) {
  const ws = useOptionalWorkspace();
  const nation = nationProp ?? ws?.workspace.nation;
  if (!nation || !partialRulesFor(module, nation)) return null;
  const label = NATION_LABELS[nation];
  return (
    <Banner tone="warning" title={`${label} rules: partial`} className="mb-4 rounded-lg">
      Seventide follows the rules for England in full. Some {label} rules for this area are not fully built in
      yet, so check figures and deadlines with your council, health board or an adviser.
    </Banner>
  );
}

/**
 * Read-only / archive banner (FR-13.2, FR-10.1). Mounted once in the app shell; module pages don't need it.
 * GDPR export and deletion stay available.
 */
export function ReadOnlyBanner() {
  const ws = useOptionalWorkspace();
  const block = useAccessBlock();
  const { message } = useReadOnlyFlag();
  if (!block) return null;
  if (block === 'archive') {
    return (
      <Banner tone="info" title="This workspace is an archive">
        Records are kept as they were. Only the owner can make corrections, and every correction is logged.
      </Banner>
    );
  }
  const canBill = ws?.workspace.permissions?.billing?.view;
  return (
    <Banner
      tone="warning"
      title="Read-only: the free trial or subscription has ended"
      action={
        ws && canBill ? (
          <RouterLink to={`/w/${ws.wsId}/more/subscription`} className="font-bold text-primary underline underline-offset-4">
            Renew the subscription
          </RouterLink>
        ) : undefined
      }
    >
      {message ??
        'You can still see everything, and download or delete your data. Adding new records and creating packs is paused until the subscription is renewed.'}
      {ws && !canBill && ' Ask the workspace owner to renew it.'}
    </Banner>
  );
}
